const rubricSolutionSellingDescription = `
Solution Selling is a sales methodology that focuses on addressing the customer's specific needs and challenges by offering tailored solutions rather than just pushing a product or service. This approach is consultative and customer-centric, aiming to build a deeper understanding of the customer's business and how the salesperson's offerings can deliver value. The key principles of solution selling include:

1. **Customer Understanding:** Deeply understanding the customer's business, industry, and specific pain points.
2. **Consultative Approach:** Acting as a consultant or advisor rather than just a salesperson.
3. **Needs Identification:** Identifying and clarifying the customer's needs and requirements.
4. **Tailored Solutions:** Presenting customized solutions that specifically address the identified needs.
5. **Value Communication:** Clearly articulating the value and benefits of the proposed solution.
6. **Relationship Building:** Building long-term relationships based on trust and mutual benefit.
7. **Problem-Solving:** Continuously engaging with the customer to solve problems and improve their business.`;

const rubricSolutionSellingList = [
  'Customer Understanding',
  'Consultative Approach',
  'Needs Identification',
  'Tailored Solutions',
  'Value Communication',
  'Relationship Building',
  'Problem-Solving',
];

const rubricColdCallingDescription = `
The ACE Cold Calling Methodology is designed to help sales professionals effectively capture attention, build connection, and engage prospects during cold calls. This approach emphasizes a structured yet flexible framework that aligns with human psychology, ensuring smooth, impactful sales conversations tailored to the prospect’s needs. The key principles include:
1. **Attention Capture**: Quickly grabbing the prospect’s attention within the first few seconds of the call.
2. **Tone Mastery**: Using the right tone to establish rapport, convey confidence, and ensure the prospect feels comfortable.
3. **Empathy and Understanding**: Demonstrating an understanding of the prospect's situation and needs through careful listening and appropriate responses.
4. **Psychological Triggers**: Leveraging human psychology, such as asking for help, to increase engagement and cooperation from the prospect.
5. **Clear Purpose Communication**: Clearly stating the purpose of the call and setting expectations for the conversation.
6. **Effective Questioning**: Asking insightful questions to uncover the prospect's pain points and needs.
7. **Value Proposition Delivery**: Clearly articulating how your product or service can address the prospect's needs and add value.
8. **Smooth Transitioning**: Guiding the conversation toward the next steps with confidence and clarity.
---
### Rubric for Evaluating a Cold Call Using the ACE Methodology
1. **Attention Capture**
   - *Objective*: To grab the prospect’s attention within the first few seconds of the call.
   - **1 - Poor**: Confusing or unengaging
   - **2 - Average**: Generic and lacks impact
   - **3 - Good**: Clear but not highly engaging
   - **4 - Excellent**: Engaging, grabs immediate attention
2. **Tone Mastery**
   - *Objective*: To use a tone that builds rapport, conveys confidence, and makes the prospect comfortable.
   - **1 - Poor**: Flat or inappropriate tone
   - **2 - Average**: Inconsistent, lacks warmth
   - **3 - Good**: Consistent but lacks confidence
   - **4 - Excellent**: Confident, warm, and natural
3. **Empathy and Understanding**
   - *Objective*: To show understanding of the prospect’s situation and needs through careful listening and appropriate responses.
   - **1 - Poor**: Interrupts or ignores cues
   - **2 - Average**: Listens but with limited empathy
   - **3 - Good**: Listens well, responds somewhat appropriately
   - **4 - Excellent**: Deeply listens and responds with empathy
4. **Psychological Triggers**
   - *Objective*: To use psychological techniques, such as asking for help, to increase engagement and cooperation from the prospect.
   - **1 - Poor**: Fails to utilize
   - **2 - Average**: Attempts but awkwardly
   - **3 - Good**: Uses effectively but not seamlessly
   - **4 - Excellent**: Seamlessly integrates, highly effective
5. **Clear Purpose Communication**
   - *Objective*: To clearly state the purpose of the call and set expectations for the conversation.
   - **1 - Poor**: Unclear or confusing
   - **2 - Average**: Somewhat clear, lacks impact
   - **3 - Good**: Clear, but not compelling
   - **4 - Excellent**: Crystal clear, compelling, and concise
6. **Effective Questioning**
   - *Objective*: To ask insightful questions that uncover the prospect’s pain points and needs.
   - **1 - Poor**: Irrelevant or off-topic
   - **2 - Average**: Relevant but lacks depth
   - **3 - Good**: Good questions, some depth
   - **4 - Excellent**: Highly relevant, deep, and insightful
7. **Value Proposition Delivery**
   - *Objective*: To clearly articulate how your product or service can address the prospect’s needs and add value.
   - **1 - Poor**: Unclear or generic
   - **2 - Average**: Somewhat clear, lacks impact
   - **3 - Good**: Clear, addresses some needs
   - **4 - Excellent**: Highly clear, compelling, addresses all key needs
8. **Smooth Transitioning**
   - *Objective*: To guide the conversation smoothly towards the next steps with confidence and clarity.
   - **1 - Poor**: Fails to close or does so awkwardly
   - **2 - Average**: Attempts to close, lacks confidence
   - **3 - Good**: Smooth but not compelling
   - **4 - Excellent**: Confident, clear, and compelling close
---
### Overall Scoring and Feedback
- **Score Interpretation**:
   - **24-32**: Excellent call, highly likely to convert.
   - **16-23**: Good call, potential for conversion with improvement in specific areas.
   - **8-15**: Average call, needs significant improvement.
   - **1-7**: Poor call, major areas for improvement.
- **Feedback**: Provide specific examples from the call for each stage, highlighting strengths and areas for improvement. Encourage continuous practice and self-assessment using the rubric.
This methodology and rubric provide a structured approach to evaluating and improving cold calling, ensuring that sales reps focus on building connections, effectively engaging prospects, and driving successful outcomes.
`;

const rubricColdCallingList = [
  'Attention Capture',
  'Tone Mastery',
  'Empathy and Understanding',
  'Psychological Triggers',
  'Clear Purpose Communication',
  'Effective Questioning',
  'Value Proposition Delivery',
  'Smooth Transitioning',
];

export const initMethodologyData = [
  {
    title: 'ACE Cold Calling',
    description: rubricColdCallingDescription,
    subData: {
      rubrics: rubricColdCallingList,
    },
  },
  {
    title: 'Solution Selling',
    description: rubricSolutionSellingDescription,
    subData: {
      rubrics: rubricSolutionSellingList,
    },
  },
];
